/**
 * Landing footer — working public routes only.
 * System status links to the **status subdomain** (not portal /status).
 */

'use client'

import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ArrowUpOutlined } from '@ant-design/icons'

type SystemStatus = 'ok' | 'warn' | 'down' | 'maintenance'

function resolveStatus(raw: string): SystemStatus {
  const v = raw.toLowerCase()
  if (v === 'down' || v === 'outage' || v === 'error') return 'down'
  if (v === 'warn' || v === 'degraded' || v === 'warning') return 'warn'
  if (v === 'maintenance') return 'maintenance'
  return 'ok'
}

function statusPageHref(): string {
  return (
    process.env.NEXT_PUBLIC_STATUS_URL?.trim() ||
    'https://status.sandbox.mechagora.net'
  )
}

const STATUS_LABEL: Record<SystemStatus, string> = {
  ok: 'All systems',
  warn: 'Degraded',
  down: 'Outage',
  maintenance: 'Maintenance',
}

/** Only routes that exist as pages (verified 200). */
const PRODUCT_LINKS = [
  { href: '/pricing', label: 'Pricing', aria: 'Client pricing' },
  { href: '/help', label: 'Help', aria: 'Help center' },
  { href: '/auth/login', label: 'Sign in', aria: 'Client sign in' },
] as const

const POLICY_LINKS = [
  { href: '/legal', label: 'Legal', aria: 'Legal information' },
  { href: '/legal/privacy', label: 'Privacy', aria: 'Privacy policy' },
  { href: '/legal/terms', label: 'Terms', aria: 'Terms of service' },
  { href: '/legal/disclosures', label: 'Disclosures', aria: 'Product disclosures' },
] as const

export const LandingFooter: React.FC = () => {
  const [isClient, setIsClient] = useState(false)
  const [status, setStatus] = useState<SystemStatus>('ok')

  useEffect(() => {
    setIsClient(true)
    // Prefer public status subdomain JSON; fall back to portal /api/status then env.
    let cancelled = false
      ; (async () => {
        const statusOrigin = statusPageHref()
        try {
          const res = await fetch(`${statusOrigin}/api/status`, {
            credentials: 'omit',
            mode: 'cors',
          })
          if (res.ok) {
            const data = (await res.json().catch(() => ({}))) as {
              overall?: string
            }
            if (cancelled) return
            if (typeof data.overall === 'string') {
              setStatus(resolveStatus(data.overall === 'operational' ? 'ok' : data.overall))
              return
            }
          }
        } catch {
          /* fall through */
        }
        try {
          const res = await fetch('/api/status', { credentials: 'same-origin' })
          if (!res.ok) return
          const data = (await res.json().catch(() => ({}))) as {
            api_degraded?: boolean
          }
          if (cancelled) return
          if (data.api_degraded) setStatus('warn')
          else setStatus(resolveStatus(process.env.NEXT_PUBLIC_STATUS || 'ok'))
        } catch {
          if (!cancelled) {
            setStatus(resolveStatus(process.env.NEXT_PUBLIC_STATUS || 'ok'))
          }
        }
      })()
    return () => {
      cancelled = true
    }
  }, [])

  const year = isClient ? new Date().getFullYear() : 2026
  const statusLabel = STATUS_LABEL[status]
  const statusMod =
    status === 'down'
      ? 'landing-status-down'
      : status === 'warn' || status === 'maintenance'
        ? 'landing-status-warn'
        : 'landing-status-ok'
  const statusHref = statusPageHref()

  return (
    <footer className="landing-footer" role="contentinfo" aria-label="Site footer">
      <div className="landing-footer-inner">
        <div className="landing-footer-brand" aria-label="Copyright">
          <Link href="/" className="landing-footer-mark" aria-label="Mechagora Agent Trading home">
            <Image
              src="/assets/brand/logo-agent-m.png"
              alt=""
              width={28}
              height={28}
              className="landing-footer-mark-img"
              loading="lazy"
            />
          </Link>
          <div className="landing-footer-brand-text">
            <span className="landing-footer-brand-name">Agent Trading</span>
            <span className="landing-footer-brand-meta">
              © {year} <span translate="no">Mechagora LLC</span>
            </span>
          </div>
        </div>

        <nav className="landing-footer-nav" aria-label="Footer links">
          <div className="landing-footer-link-group landing-footer-link-group-product">
            {PRODUCT_LINKS.map(({ href, label, aria }) => (
              <Link key={href} href={href} aria-label={aria} className="landing-footer-link">
                {label}
              </Link>
            ))}
          </div>
          <span className="landing-footer-divider" aria-hidden="true" />
          <div className="landing-footer-link-group landing-footer-link-group-policy">
            {POLICY_LINKS.map(({ href, label, aria }) => (
              <Link key={href} href={href} aria-label={aria} className="landing-footer-link">
                {label}
              </Link>
            ))}
          </div>
        </nav>

        <div className="landing-footer-actions">
          <a
            href={statusHref}
            className={`landing-status-pill ${statusMod}`}
            aria-label={`System status: ${statusLabel}`}
            title={`Open status page (${statusHref})`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="landing-status-dot" aria-hidden="true" />
            <span className="landing-status-text" aria-live="polite" aria-atomic="true">
              {statusLabel}
            </span>
          </a>

          <a
            href="#main-content"
            className="landing-top-btn"
            aria-label="Back to top"
            title="Back to top"
          >
            <ArrowUpOutlined aria-hidden="true" />
            <span className="sr-only">Back to top</span>
          </a>
        </div>
      </div>
    </footer>
  )
}
